import { createState } from "./index";

export type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

interface UIState {
  // Viewport State
  breakpoint: Breakpoint;
  isMobile: boolean; // < md (768px)
  isTablet: boolean; // md to lg
  isDesktop: boolean; // >= lg (1024px)
  width: number;

  // Layout State
  isSidebarOpen: boolean; // Mobile drawer
  isSidebarCollapsed: boolean; // Desktop collapsed

  // Actions
  setViewport: (width: number) => void;
  setSidebarOpen: (isOpen: boolean) => void;
  setSidebarCollapsed: (isCollapsed: boolean) => void;
  toggleSidebar: () => void;
}

const getBreakpoint = (width: number): Breakpoint => {
  if (width < 640) return "xs";
  if (width < 768) return "sm";
  if (width < 1024) return "md";
  if (width < 1280) return "lg";
  if (width < 1536) return "xl";
  return "2xl";
};

export const useUIStore = createState<UIState>(
  "UI",
  (set, get) => ({
    // Initial state (safe defaults for SSR)
    breakpoint: "lg",
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    width: 1200,
    isSidebarOpen: false,
    isSidebarCollapsed: false,

    setViewport: (width) => {
      const breakpoint = getBreakpoint(width);
      const isMobile = width < 768;
      const isTablet = width >= 768 && width < 1024;
      const isDesktop = width >= 1024;

      set((state) => ({
        ...state,
        width,
        breakpoint,
        isMobile,
        isTablet,
        isDesktop,
        // Auto-close mobile sidebar when moving to desktop
        isSidebarOpen: isDesktop ? false : state.isSidebarOpen,
      }));
    },

    setSidebarOpen: (isOpen) =>
      set((state) => ({
        ...state,
        isSidebarOpen: isOpen,
      })),

    setSidebarCollapsed: (isCollapsed) =>
      set((state) => ({
        ...state,
        isSidebarCollapsed: isCollapsed,
      })),

    toggleSidebar: () => {
      const { isDesktop, isSidebarOpen, isSidebarCollapsed } = get();
      if (isDesktop) {
        set((state) => ({
          ...state,
          isSidebarCollapsed: !isSidebarCollapsed,
        }));
      } else {
        set((state) => ({
          ...state,
          isSidebarOpen: !isSidebarOpen,
        }));
      }
    },
  }),
  {
    persist: true,
    partialize: (state) => ({ isSidebarCollapsed: state.isSidebarCollapsed }),
  },
);
